// Email Supplier/s templates (docs/reference/SUPPLIER_CHANGE_EMAILS.md): a company email template
// (Email Templates page) filled in for one supplier and the services it's about. Used by the
// Suppliers tab and the events lists; both open SupplierEmailsDialog, where staff untick
// suppliers or services and edit the body.

import Vue from 'vue'
import dayjs from 'dayjs'
import { defaultFinalRate, rateSuffix } from '@/utils/finalRates'

const DECLINED_STATUS = 4

function parseList(value) {
    if (Array.isArray(value)) return value
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch (e) {
            return []
        }
    }
    return []
}

const CELL = 'padding: 10px; border: 1px solid #ddd; vertical-align: top; word-break: break-word;'

function servicesTable(builds, detailsFor) {
    if (!builds || !builds.length) return ''
    let html = '<br><br><table contenteditable="false" style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; margin-top: 10px;">'
    html += '<thead><tr style="background-color: #3988AA; color: white;">'
    html += '<th style="padding: 10px; text-align: left; border: 1px solid #ddd; width: 30%;">Service</th>'
    html += '<th style="padding: 10px; text-align: left; border: 1px solid #ddd; width: 15%;">Start Date/Time</th>'
    html += '<th style="padding: 10px; text-align: left; border: 1px solid #ddd; width: 25%;">Details</th>'
    html += '<th style="padding: 10px; text-align: left; border: 1px solid #ddd; width: 30%;">Notes</th>'
    html += '</tr></thead><tbody>'
    builds.forEach((service, index) => {
        const dateTime = []
        if (service.start_date) dateTime.push(dayjs(service.start_date).format('DD/MM/YYYY'))
        if (service.start_time) dateTime.push((service.type_id == 1 ? 'Check-in: ' : '') + service.start_time)
        const notes = service.booking_notes ? service.booking_notes.replace(/\n/g, '<br>') : '—'
        html += '<tr style="background-color: ' + (index % 2 === 0 ? '#ffffff' : '#f9f9f9') + ';">'
        html += '<td style="' + CELL + '">' + (service.option ? service.option.name : '—') + '</td>'
        html += '<td style="' + CELL + '">' + (dateTime.length ? dateTime.join('<br>') : '—') + '</td>'
        html += '<td style="' + CELL + '">' + detailsFor(service) + '</td>'
        html += '<td style="' + CELL + '">' + notes + '</td>'
        html += '</tr>'
    })
    return html + '</tbody></table>'
}

// {services}: rate and groupsize per service (per night for a hotel).
function serviceDetails(service, currency) {
    if (service.type_id == 1) {
        const rates = parseList(service.accom_final_rates)
        return parseList(service.accom_groupsize).map((size, index) =>
            '<strong>Day ' + (index + 1) + ':</strong><br>&nbsp;&nbsp;<strong>Rate:</strong> ' + currency + (rates[index] || defaultFinalRate(service))
            + '<br>&nbsp;&nbsp;<strong>Groupsize:</strong> ' + size + ' pax<br><br>'
        ).join('')
    }
    const finalRate = service.final_rate !== null && service.final_rate !== undefined ? service.final_rate : defaultFinalRate(service)
    let details = ''
    if (finalRate > 0) details += '<strong>Rate:</strong> ' + currency + finalRate + rateSuffix(service) + '<br>'
    return details + '<strong>Groupsize:</strong> ' + (service.groupsize || 0) + ' pax'
}

// {services} on its own: the services table with rate and groupsize (availability requests).
export function servicesHtml(builds, currency = '') {
    return servicesTable(builds, service => serviceDetails(service, currency))
}

// Saves this close together are one edit: a hotel saves each night on its own as it's typed.
const EDIT_SESSION_MS = 10 * 60 * 1000

function serviceSizes(service, entry) {
    return service.type_id == 1
        ? parseList(entry.accom_groupsize).map(size => parseInt(size) || 0)
        : [parseInt(entry.groupsize) || 0]
}

// The groupsize before the latest edit: the history entry before that edit's run of saves (or,
// when the run starts the history, its first entry) that differs from now. An email to the
// supplier about the service (sentTimes, ms) ends a run, so the old size is what they were told.
// Null when nothing differs.
function previousSizes(service, sentTimes = []) {
    const history = [...(service.groupsize_history || [])].sort((a, b) => a.id - b.id)
    if (!history.length) return null
    const current = JSON.stringify(serviceSizes(service, service))
    const time = entry => Date.parse(entry.created_at) || 0
    let start = history.length - 1
    while (start > 0) {
        const before = time(history[start - 1])
        const after = time(history[start])
        if (after - before >= EDIT_SESSION_MS || sentTimes.some(sent => before <= sent && sent < after)) break
        start--
    }
    for (let i = start > 0 ? start - 1 : 0; i >= 0; i--) {
        const sizes = serviceSizes(service, history[i])
        if (JSON.stringify(sizes) !== current) return sizes
    }
    return null
}

// {groupsize_history}: old vs new groupsize (per night for a hotel).
function groupsizeChange(service, sentTimes = []) {
    const old = previousSizes(service, sentTimes)
    if (!old) return '<em>No change</em>'
    if (service.type_id == 1) {
        return serviceSizes(service, service).map((size, index) =>
            '<strong>Day ' + (index + 1) + ':</strong><br>'
            + '&nbsp;&nbsp;<strong>Old Groupsize:</strong> ' + (old[index] || 0) + ' pax<br>'
            + '&nbsp;&nbsp;<strong>New Groupsize:</strong> ' + size + ' pax<br><br>'
        ).join('')
    }
    return '<strong>Old Groupsize:</strong> ' + old[0] + ' pax<br><strong>New Groupsize:</strong> ' + (parseInt(service.groupsize) || 0) + ' pax'
}

export const LINK_FALLBACK = 'If the button above does not work, please copy and paste the following link into your web browser:'

// A button with its URL below, for suppliers whose mail client or network breaks the button.
function linkButton(href, label, colour) {
    return '<a href="' + href + '" style="display: inline-block; padding: 12px 24px; background-color: ' + colour + '; color: white; text-decoration: none; border-radius: 5px; font-weight: bold; font-size: 16px;">' + label + '</a>'
        + '<br><br><span style="font-size: 13px;">' + LINK_FALLBACK + '</span>'
        + '<br><a href="' + href + '" style="font-size: 13px; word-break: break-all;">' + href + '</a>'
}

/**
 * A template's body with its placeholders filled in for one supplier.
 * options: { event, supplier, builds, currency, sentTimes(build id) -> [ms] when the supplier was emailed about it }
 */
export function fillSupplierTemplate(body, { event, supplier, builds, currency = '', sentTimes = null }) {
    if (!body) return ''
    const lead = (event && event.lead) || {}
    const buildIds = (builds || []).map(build => build.id).join(',')
    const linkPath = `${event ? event.id : ''}/${supplier ? supplier.id : ''}/${buildIds}`
    const appUrl = window.location.origin
    const leadName = lead.first_name ? lead.first_name + ' ' + (lead.last_name ?? '') : ''

    const replacements = {
        '{supplier_name}': supplier ? supplier.supplier_name : '',
        '{reference_code}': event ? event.reference_code : '',
        '{event_name}': event ? (event.name || leadName) : '',
        '{lead_name}': leadName ? leadName + (lead.phone ? ' - ' + lead.phone : '') : '',
        '{event_date}': lead.start_date ? dayjs(lead.start_date).format('DD/MM/YYYY') : '',
        '{location}': lead.location ? lead.location.name : '',
        '{type}': lead.activity_type || '',
        '{confirm_link}': linkButton(appUrl + '/supplier_confirm/' + linkPath, 'Confirm Booking', '#4CAF50'),
        '{decline_link}': '',
        '{services}': servicesTable(builds, service => serviceDetails(service, currency)),
        '{groupsize_history}': servicesTable(builds, service => groupsizeChange(service, sentTimes ? sentTimes(service.id) : [])),
        '{confirm_cancellation_link}': linkButton(appUrl + '/supplier_confirm_cancellation/' + linkPath, 'Confirm Cancellation', '#C62828'),
        '{confirm_payment_link}': linkButton(appUrl + '/supplier_confirm_payment/' + linkPath, 'Confirm Payment', '#2196F3'),
    }
    return Object.keys(replacements).reduce((parsed, placeholder) => parsed.split(placeholder).join(replacements[placeholder] ?? ''), body)
}

// A service as SupplierEmailsDialog lists it, ticked unless declined (or `selected` says).
// "15 pax", or "15 / 12 pax (per night)" when a hotel's nights differ; null when there's none.
export function groupsizeLabel(groupsize) {
    const list = parseList(groupsize).map(size => parseInt(size) || 0)
    if (list.length) {
        return list.every(size => size === list[0]) ? `${list[0]} pax` : `${list.join(' / ')} pax (per night)`
    }
    if (groupsize === null || groupsize === undefined || groupsize === '') return null
    return `${parseInt(groupsize) || 0} pax`
}

// A hotel's nights, as the Suppliers tab counts them: check-in to check-out, else one per
// per-night groupsize.
function accomNights(build) {
    if (build.start_date && build.end_date) {
        const nights = dayjs(build.end_date).diff(dayjs(build.start_date), 'day')
        if (nights > 0) return nights
    }
    return parseList(build.accom_groupsize).length || 1
}

// "Fri 04/12/2026", or a hotel's nights: "Fri 04/12/2026 – Sat 05/12/2026 (2 nights)".
export function serviceDates(build) {
    if (!build.start_date) return null
    const first = dayjs(build.start_date)
    const nights = build.type_id == 1 ? accomNights(build) : 1
    if (nights < 2) return first.format('ddd DD/MM/YYYY')
    return `${first.format('ddd DD/MM/YYYY')} – ${first.add(nights - 1, 'day').format('ddd DD/MM/YYYY')} (${nights} nights)`
}

// The final rate as the Suppliers tab shows it (and {services} sends it): per night for a hotel
// ("£120", or "£120 / £100 per night" when nights differ), per person or per group otherwise.
export function finalRateLabel(build, currency = '') {
    if (build.type_id == 1) {
        const rates = parseList(build.accom_final_rates)
        const nights = accomNights(build)
        const list = Array.from({ length: nights }, (_, index) => Number(rates[index]) || defaultFinalRate(build) || 0)
        if (!list.some(rate => rate > 0)) return null
        return list.every(rate => rate === list[0])
            ? `${currency}${list[0]}`
            : `${list.map(rate => currency + rate).join(' / ')} per night`
    }
    const rate = build.final_rate !== null && build.final_rate !== undefined ? build.final_rate : defaultFinalRate(build)
    return rate > 0 ? `${currency}${rate}${rateSuffix(build)}` : null
}

// A hotel stay of more than one night, a row per night as on the Suppliers tab:
// [{ date, rate, groupsize }]. Null for one night or anything else. A rate only with currency
// known (the prompt's changes have no rates).
export function accomNightRows(build, currency = null) {
    if (build.type_id != 1 || !build.start_date) return null
    const nights = accomNights(build)
    if (nights < 2) return null
    const rates = parseList(build.accom_final_rates)
    const sizes = parseList(build.accom_groupsize)
    return Array.from({ length: nights }, (_, index) => {
        const rate = Number(rates[index]) || defaultFinalRate(build) || 0
        return {
            date: dayjs(build.start_date).add(index, 'day').format('ddd DD/MM/YYYY'),
            rate: currency !== null && rate > 0 ? `${currency}${rate}` : null,
            groupsize: sizes[index] !== undefined ? `${parseInt(sizes[index]) || 0} pax` : null,
        }
    })
}

export function serviceOption(build, currency = '', selected = null) {
    const nights = accomNightRows(build, currency)
    return {
        id: build.id,
        build_id: build.id,
        name: build.option ? build.option.name : '—',
        // A stay of several nights lists them in `nights` instead.
        nights,
        date: nights ? null : serviceDates(build),
        time: build.start_time || null,
        rate: nights ? null : finalRateLabel(build, currency),
        groupsize: nights ? null : groupsizeLabel(build.type_id == 1 ? build.accom_groupsize : build.groupsize),
        tag: build.booking_status_id === DECLINED_STATUS ? 'Cancelled' : null,
        selected: selected ?? build.booking_status_id !== DECLINED_STATUS,
    }
}

/**
 * SupplierEmailsDialog items for a template (null: a blank email), one per supplier.
 * groups: [[build, ...], ...] one array per supplier.
 * options: { event, currency, selected(builds) -> bool, serviceSelected(build) -> bool }
 */
export function templateEmailItems(groups, template, { event, currency = '', selected = null, serviceSelected = null }) {
    return groups.filter(builds => builds.length && builds[0].supplier).map(builds => {
        const supplier = builds[0].supplier
        const services = builds.map(build => serviceOption(build, currency, serviceSelected ? serviceSelected(build) : null))
        return {
            key: supplier.id,
            supplier,
            selected: selected ? selected(builds) : services.some(service => service.selected),
            services,
            compose: (ids, sentTimes = null) => {
                const chosen = builds.filter(build => ids.includes(build.id))
                return {
                    subject: template ? template.subject : '',
                    body: template ? fillSupplierTemplate(template.body, { event, supplier, builds: chosen, currency, sentTimes }) : '',
                    build_ids: chosen.map(build => build.id),
                }
            },
        }
    })
}

// The dialog's note for a template: send_supplier_email (no email_type) sets booking or payment
// statuses by the subject.
export function templateEmailNote(template) {
    const subject = template ? template.subject || '' : ''
    if (subject.includes('Cancellation')) {
        return 'Sending sets the ticked services to <b>Cancellation Pending</b> until the supplier clicks Confirm Cancellation, then <b>Cancelled</b>.'
    }
    const notes = []
    if (subject.includes('Booking')) notes.push('Sending sets the ticked services to <b>Requested</b>.')
    if (subject.includes('Payment')) notes.push('Sending sets the ticked services\' payment status to <b>Notification Sent</b>.')
    return notes.join('<br>')
}

// Which services suppliers were already emailed about on an event (get_sent_services):
// [{ supplier_id, build_ids, subject, created_at }], newest first; [] when it fails.
export async function loadSentServices(companyId, eventId, supplierIds) {
    const supplier_ids = [...new Set(supplierIds)].filter(Boolean)
    if (!eventId || !supplier_ids.length) return []
    const response = await Vue.axios.get('admin/events/get_sent_services', {
        company_id: companyId,
        event_id: eventId,
        supplier_ids,
    })
    const data = response && response.data
    return data && data.response === true ? data.data : []
}

// The same for every supplier on a page of events, in one request: { event_id: [entries] }.
export async function loadSentServicesForEvents(companyId, eventIds) {
    const event_ids = [...new Set(eventIds)].filter(Boolean)
    if (!event_ids.length) return {}
    const response = await Vue.axios.get('admin/events/get_sent_services', { company_id: companyId, event_ids })
    const data = response && response.data
    const byEvent = {}
    if (data && data.response === true) {
        data.data.forEach(entry => {
            if (!byEvent[entry.event_id]) byEvent[entry.event_id] = []
            byEvent[entry.event_id].push(entry)
        })
    }
    return byEvent
}

// The company's email templates. Rejects when the request fails.
export async function loadEmailTemplates(companyId) {
    const response = await Vue.axios.get(`admin/email_templates/get_email_templates/${companyId}`)
    const data = response && response.data
    if (!data || data.response !== true) throw new Error('Failed to load email templates')
    return data.data || []
}
